interface VersionInfo {
  version: string;
  buildTime: number;
  hash: string;
}

interface UpdateServiceConfig {
  pollingIntervalMs: number;
  maxPollingIntervalMs: number;
  backoffMultiplier: number;
  versionEndpoint: string;
}

class UpdateService {
  private config: UpdateServiceConfig;
  private currentVersion: VersionInfo | null = null;
  private pollingTimer: NodeJS.Timeout | null = null;
  private currentPollingInterval: number;
  private isPolling = false;
  private visibilityChangeHandler: (() => void) | null = null;

  constructor(config?: Partial<UpdateServiceConfig>) {
    this.config = {
      pollingIntervalMs: 30 * 60 * 1000, // 30 minutes
      maxPollingIntervalMs: 2 * 60 * 60 * 1000, // 2 hours
      backoffMultiplier: 1.5,
      versionEndpoint: '/version.json',
      ...config,
    };
    this.currentPollingInterval = this.config.pollingIntervalMs;
  }

  async getCurrentVersion(): Promise<VersionInfo | null> {
    if (this.currentVersion) {
      return this.currentVersion;
    }

    try {
      // Try to get version from build process or package.json
      const response = await fetch(this.config.versionEndpoint, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch version: ${response.status}`);
      }

      this.currentVersion = await response.json();
      return this.currentVersion;
    } catch (error) {
      console.warn('Failed to get current version:', error);
      // Fallback to package.json version if available
      this.currentVersion = {
        version: '1.0.0',
        buildTime: Date.now(),
        hash: 'unknown',
      };
      return this.currentVersion;
    }
  }

  async checkForUpdates(): Promise<boolean> {
    try {
      const currentVersion = await this.getCurrentVersion();
      if (!currentVersion) {
        return false;
      }

      // Fetch latest version from server
      const response = await fetch(this.config.versionEndpoint, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to check for updates: ${response.status}`);
      }

      const latestVersion: VersionInfo = await response.json();

      // Compare versions - any difference in hash or build time indicates an update
      const hasUpdate =
        latestVersion.hash !== currentVersion.hash ||
        latestVersion.buildTime > currentVersion.buildTime ||
        latestVersion.version !== currentVersion.version;

      if (hasUpdate) {
        // Reset polling interval on successful update detection
        this.currentPollingInterval = this.config.pollingIntervalMs;
      } else {
        // Increase polling interval with exponential backoff
        this.currentPollingInterval = Math.min(
          this.currentPollingInterval * this.config.backoffMultiplier,
          this.config.maxPollingIntervalMs
        );
      }

      return hasUpdate;
    } catch (error) {
      console.warn('Update check failed:', error);
      // Increase polling interval on error
      this.currentPollingInterval = Math.min(
        this.currentPollingInterval * this.config.backoffMultiplier,
        this.config.maxPollingIntervalMs
      );
      return false;
    }
  }

  startPolling(onUpdateFound: () => void): void {
    if (this.isPolling) {
      return;
    }

    this.isPolling = true;

    // Set up visibility change handler to pause/resume polling
    this.visibilityChangeHandler = () => {
      if (document.hidden) {
        this.pausePolling();
      } else {
        this.resumePolling(onUpdateFound);
      }
    };

    document.addEventListener('visibilitychange', this.visibilityChangeHandler);

    // Start polling if page is visible
    if (!document.hidden) {
      this.resumePolling(onUpdateFound);
    }
  }

  private resumePolling(onUpdateFound: () => void): void {
    if (this.pollingTimer) {
      return;
    }

    const poll = async () => {
      try {
        const hasUpdate = await this.checkForUpdates();
        if (hasUpdate) {
          onUpdateFound();
          this.stopPolling(); // Stop polling once update is found
          return;
        }
      } catch (error) {
        console.warn('Polling error:', error);
      }

      // Schedule next poll
      this.pollingTimer = setTimeout(poll, this.currentPollingInterval);
    };

    // Initial check
    poll();
  }

  private pausePolling(): void {
    if (this.pollingTimer) {
      clearTimeout(this.pollingTimer);
      this.pollingTimer = null;
    }
  }

  stopPolling(): void {
    this.isPolling = false;
    this.pausePolling();

    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      this.visibilityChangeHandler = null;
    }
  }

  refreshApp(): void {
    // Clear any cached data
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
      });
    }

    // Force reload
    window.location.reload();
  }

  // Development helper methods
  simulateUpdate(): void {
    if (process.env.NODE_ENV === 'development') {
      // Force current version to be different from "server" version
      this.currentVersion = {
        version: '0.9.0',
        buildTime: Date.now() - 60000,
        hash: 'old-hash',
      };
    }
  }

  getCurrentPollingInterval(): number {
    return this.currentPollingInterval;
  }
}

// Create singleton instance
export const updateService = new UpdateService();

// Export types for use in components
export type { VersionInfo, UpdateServiceConfig };